import Amplify from 'aws-amplify'
import moment from 'moment'
import { displayMessage } from '../helpers/helper.js'
import { i18n } from '@/i18n'

export const LocationService = {
  async getAllLocations(accessToken) {
    try {
      // get all locations
      let myInit = {
        headers: {
          Authorization: 'Bearer ' + accessToken,
        },
        queryStringParameters: {
          itemsPerPage: false,
        },
      }
      const res = await Amplify.API.get('locations', '/v1/int/locations', myInit)
      return res?.data
    } catch (error) {
      console.log(error)
      displayMessage(i18n.t('order.anErrorOccurredLoadingLocation'))
    }
  },

  /**
   * Fetches the tenant zoning exposed as a path-based tree
   * (`country_id > country_area_1 > country_area_2`) used to drive the cascading
   * input on the location form. An optional `countryId` restricts the tree to a
   * single country.
   *
   * @param {string} accessToken - The user's access token for authorization.
   * @param {string} [countryId] - Optional ISO 3166-1 alpha-3 country code filter.
   * @returns {Promise<Array>} - The zoning tree, or an empty array on error.
   */
  async getAreas(accessToken, countryId) {
    let myInit = {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    }
    if (countryId) {
      myInit.queryStringParameters = { country_id: countryId }
    }
    try {
      const res = await Amplify.API.get('locations', '/v1/int/locations/areas', myInit)
      return res?.data ?? []
    } catch (error) {
      console.log(error)
      displayMessage(i18n.t('order.anErrorOccurredLoadingLocation'))
      return []
    }
  },

  /**
   * Fetches the current settings, including special closures, for the user.
   * It formats the closure dates and sorts them by active status and date.
   * Displays a snackbar message if an error occurs.
   *
   * @param {string} accessToken - The user's access token for authorization.
   * @returns {Promise<Object>} - The settings response object from the API.
   *
   * @example
   * const settings = await getSettings(accessToken);
   */
  async getSettings(accessToken) {
    let myInit = {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    }
    try {
      let res = await Amplify.API.get('locations', '/v1/int/current/settings', myInit)
      if (Array.isArray(res.global_special_closures)) {
        res.global_special_closures = res.global_special_closures.sort(newestToOldestButActiveClosuresFirst())
        formatClosuresDates(res, 'YYYY-MM-DD HH:mm')
      }
      return res
    } catch (error) {
      console.log(error)
      displayMessage(error.message)
    }
  },

  /**
   * Retrieves location details by location ID.
   *
   * Sends a GET request to the `/v1/int/locations/:locationId` endpoint using Amplify's API.
   * If the request fails, it logs the error and displays a user-friendly error message.
   *
   * @async
   * @function getLocation
   * @param {string} accessToken - Access token used for authenticating the API request.
   * @param {string|number} locationId - Unique identifier of the location to retrieve.
   * @param {Object} params - Optional query string parameters (e.g., { fields: ['name', 'location_id'] }).
   * @returns {Promise<Object|undefined>} - The location data if the request is successful, otherwise `undefined`.
   *
   * @throws {Error} - Logs the error and displays a localized error message if the request fails.
   */
  async getLocation(accessToken, locationId, params = {}) {
    let path = '/v1/int/locations/' + locationId
    let myInit = {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    }

    if (Object.keys(params).length > 0) {
      myInit.queryStringParameters = params
    }

    try {
      return await Amplify.API.get('locations', path, myInit)
    } catch (error) {
      console.log(error)
      displayMessage(i18n.t('order.anErrorOccurredLoadingLocation'))
    }
  },
  /**
   * Retrieves a list of all locations using the provided query parameters.
   * Displays a snackbar message if an error occurs.
   *
   * @param {string} accessToken - The user's access token for authorization.
   * @param {Object} queryStringParameters - The query string parameters for the request.
   * @returns {Promise<Object>} - The response object with location data.
   *
   * @example
   * const locations = await getLocations(accessToken, { city: 'New York' });
   */
  async getLocations(accessToken, queryStringParameters) {
    // get all locations
    try {
      let myInit = {
        headers: {
          Authorization: 'Bearer ' + accessToken,
        },
        queryStringParameters: queryStringParameters,
      }
      return await Amplify.API.get('locations', '/v1/int/locations', myInit)
    } catch (error) {
      console.log(error)
      displayMessage(i18n.t('order.anErrorOccurredLoadingLocation'))
    }
  },

  /**
   * Updates the settings, including global special closures, with new values.
   * The closure dates are formatted to the required format before sending.
   * Displays a snackbar message upon success or failure.
   *
   * @param {string} accessToken - The user's access token for authorization.
   * @param {Object} settings - The new settings to update, including global special closures.
   * @returns {Promise<Object>} - The response object from the API.
   *
   * @example
   * const updatedSettings = await updateSettings(accessToken, { globalSpecialClosures: [...] });
   */
  async updateSettings(accessToken, settings) {
    // update settings with new values
    try {
      let myInit = {
        headers: {
          Authorization: 'Bearer ' + accessToken,
        },
        body: {
          global_special_closures: settings?.globalSpecialClosures.map((specialClosure) => {
            return {
              begin: moment(new Date(specialClosure.begin)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z'),
              end: moment(new Date(specialClosure.end)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z'),
              locations: specialClosure.locations,
            }
          }),
          hours: {
            displayed_services: settings?.hours?.displayed_services || [],
          },
        },
      }
      const res = await Amplify.API.put('locations', '/v1/int/current/settings', myInit)
      if (Array.isArray(res.global_special_closures)) {
        res.global_special_closures = res.global_special_closures.sort(newestToOldestButActiveClosuresFirst())
        formatClosuresDates(res, 'YYYY-MM-DD HH:mm')
      }
      displayMessage(i18n.t('settings.settingsUpdated'), 'success')
      return res
    } catch (error) {
      console.log(error)
      const apiMessage = error.response?.data?.message
      const errorMap = {
        some_special_closures_overlap_or_are_incorrect: i18n.t('location.specialClosuresOverlap'),
      }
      displayMessage(errorMap[apiMessage] || error.message)
    }
  },
}

/**
 * Sorts closures such that active closures come first, followed by future closures,
 * and finally inactive closures. Active closures are sorted by their end date (furthest in the future),
 * future closures are sorted by their start date (nearest to furthest), and inactive closures
 * are sorted by their end date (most recently ended to oldest).
 *
 * @returns {function} - A comparison function that can be used with array sort.
 * The comparison logic is as follows:
 *   1. Active closures (those currently ongoing) are prioritized.
 *   2. Among active closures, those that will end the earliest are prioritized.
 *   3. Future closures (those that haven't started yet) come next, sorted by the nearest start date.
 *   4. Inactive closures (those that have ended) come last, sorted by the most recent end date.
 *
 * @param {Object} closure1 - The first closure object to compare.
 * @param {Object} closure2 - The second closure object to compare.
 * @param {string} closure1.begin - The start date of the first closure (ISO string format).
 * @param {string} closure1.end - The end date of the first closure (ISO string format).
 * @param {string} closure2.begin - The start date of the second closure (ISO string format).
 * @param {string} closure2.end - The end date of the second closure (ISO string format).
 *
 * @example
 * closuresArray.sort(newestToOldestButActiveClosuresFirst());
 */
function newestToOldestButActiveClosuresFirst() {
  return (closure1, closure2) => {
    // Determine if closures are inactive (i.e., their end date is in the past)
    const closure1IsInactive = new Date() - new Date(closure1.end) > 0
    const closure2IsInactive = new Date() - new Date(closure2.end) > 0
    const bothClosuresAreInactive = closure1IsInactive && closure2IsInactive

    // Determine if closures are in the future (i.e., their start date is in the future)
    const closure1IsFuture = new Date() - new Date(closure1.begin) < 0
    const closure2IsFuture = new Date() - new Date(closure2.begin) < 0
    const bothClosuresAreFuture = closure1IsFuture && closure2IsFuture

    // If both closures are inactive, sort them by the most recently ended first
    if (bothClosuresAreInactive) {
      // Sort by end date, descending (newest to oldest)
      return new Date(closure2.end) - new Date(closure1.end)
    }
    if (closure1IsInactive) {
      // If only the first closure is inactive, it comes later in the order
      return 1
    }
    if (closure2IsInactive) {
      // If only the second closure is inactive, it comes later in the order
      return -1
    }
    // If neither closure is inactive (both are active or future)
    if (bothClosuresAreFuture) {
      // If both closures are in the future, sort by the soonest to start
      return new Date(closure1.begin) - new Date(closure2.begin)
    }
    if (closure1IsFuture) {
      // If only the first closure is in the future, it comes later
      return 1
    }
    if (closure2IsFuture) {
      // If only the second closure is in the future, it comes later
      return -1
    }
    // If both closures are currently active (i.e., already started but not finished),
    // sort by the one that will end later (earliest in the future)
    return new Date(closure1.end) - new Date(closure2.end)
  }
}

/**
 * Formats the `begin` and `end` dates of each closure in the `global_special_closures` array.
 * Dates are converted to ISO 8601 format (`YYYY-MM-DDTHH:mm:ss`).
 *
 * @param {Object} res - The response object containing closure data.
 * @param {Array<Object>} res.global_special_closures - Array of closure objects.
 * @param {string} res.global_special_closures[].begin - The start date of a closure (ISO string format).
 * @param {string} res.global_special_closures[].end - The end date of a closure (ISO string format).
 *
 * @example
 * const response = {
 *   global_special_closures: [
 *     { begin: '2023-10-01T08:00:00', end: '2023-10-10T18:00:00' }
 *   ]
 * };
 * formatClosuresDates(response);
 */
function formatClosuresDates(res, format) {
  for (let specialClosure of res?.global_special_closures ?? []) {
    specialClosure.begin = moment(new Date(specialClosure.begin)).format(format)
    specialClosure.end = moment(new Date(specialClosure.end)).format(format)
  }
}
